import React, { useEffect, useState, useMemo } from 'react';
import {
    Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
    TextField, Typography, Stack, Paper, CircularProgress,
    IconButton, Tooltip, Chip, Avatar, InputAdornment, Divider
} from '@mui/material';
import {
    CloudUpload, Delete, Visibility, Search,
    CheckCircle, ErrorOutline, Close
} from '@mui/icons-material';
import TablePagination from '@mui/material/TablePagination';
import { db, auth, storage } from '../../firebase';
import {
    collection, getDocs, query, where, doc, getDoc,
    updateDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { toast } from 'react-toastify';

function DocumentUpload() {
    const [students, setStudents] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [schoolId, setSchoolId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeYear, setActiveYear] = useState("");
    const [fetching, setFetching] = useState(false);

    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [open, setOpen] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [files, setFiles] = useState({});

    const fetchSchoolId = async () => {
        const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        if (userDoc.exists()) {
            const sId = userDoc.data().schoolId;
            setSchoolId(sId);

            // Fetch the active academic year
            const yearQuery = query(
                collection(db, "academicyears"),
                where("schoolId", "==", sId),
                where("status", "==", "Active")
            );
            const yearSnap = await getDocs(yearQuery);
            if (!yearSnap.empty) {
                setActiveYear(yearSnap.docs[0].data().name);
            }
        }
    };

    const fetchStudents = async (sId, year) => {
        if (!sId || !year) return; // Ensure we have both before querying
        setFetching(true);
        try {
            // Query admission collection using both School ID and Academic Year
            const q = query(
                collection(db, 'admission'),
                where('schoolId', '==', sId),
                where('academicYear', '==', year),
                where("status", "==", "Active")

            );

            const snap = await getDocs(q);
            const admissionsData = snap.docs.map(d => ({ id: d.id, ...d.data() }));

            const studentPromises = admissionsData.map(async (adm) => {
                if (!adm.studentId) return adm;
                const studentSnap = await getDoc(doc(db, 'students', adm.studentId));
                if (studentSnap.exists()) {
                    const s = studentSnap.data();
                    return {
                        ...adm,
                        studentName: `${s.title || ''} ${s.firstname || ''} ${s.fathername || ''} ${s.surname || ''}`.trim(),
                    };
                }
                return adm;
            });

            const finalData = await Promise.all(studentPromises);
            setStudents(finalData);
        } catch (error) {
            console.error(error);
            toast.error("Failed to load students for " + year);
        } finally {
            setFetching(false);
        }
    };

    const fetchDocuments = async (sId) => {
        const q = query(collection(db, 'documents'), where('schoolId', '==', sId));
        const snap = await getDocs(q);
        setDocuments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };

    useEffect(() => { fetchSchoolId(); }, []);
    useEffect(() => {
        if (schoolId && activeYear) {
            fetchStudents(schoolId, activeYear);
            fetchDocuments(schoolId);
        }
    }, [schoolId, activeYear]);

    const handleOpen = (student) => {
        setSelectedStudent(student);
        setFiles({});
        setOpen(true);
    };

    const handleClose = () => { setOpen(false); setSelectedStudent(null); };

    const handlePreview = (url) => {
        setPreviewUrl(url);
        setPreviewOpen(true);
    };

    // 🔥 HIGH SPEED UPLOAD LOGIC
    const handleUpload = async () => {
        if (!selectedStudent || Object.keys(files).length === 0) return;
        setLoading(true);
        try {
            const uploadedDocs = { ...(selectedStudent.documents || {}) };

            // Map files to parallel upload promises
            const uploadPromises = Object.entries(files).map(async ([docname, file]) => {
                const storageRef = ref(storage, `documents/${schoolId}/${selectedStudent.id}/${docname}_${Date.now()}`);
                // Start upload
                const snapshot = await uploadBytes(storageRef, file);
                const url = await getDownloadURL(snapshot.ref);
                return { docname, url, path: storageRef.fullPath };
            });

            // Wait for ALL uploads to finish at the same time
            const results = await Promise.all(uploadPromises);

            results.forEach(({ docname, url, path }) => {
                uploadedDocs[docname] = { url, path };
            });

            // Single Firestore update for all documents
            await updateDoc(doc(db, 'admission', selectedStudent.id), { documents: uploadedDocs });

            toast.success("Documents uploaded instantly! ⚡");
            fetchStudents(schoolId);
            handleClose();
        } catch (err) {
            console.error(err);
            toast.error("Upload failed. Check connection.");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteDoc = async (studentId, docname, path) => {
        if (!window.confirm(`Delete ${docname}?`)) return;
        try {
            await deleteObject(ref(storage, path));
            const studentRef = doc(db, 'admission', studentId);
            const studentSnap = await getDoc(studentRef);
            let currentDocs = studentSnap.data().documents || {};
            delete currentDocs[docname];
            await updateDoc(studentRef, { documents: currentDocs });
            toast.success("Deleted");
            fetchStudents(schoolId);
        } catch (err) {
            toast.error("Delete failed");
        }
    };

    const filtered = useMemo(() => {
        return students.filter(s => (s.studentName || '').toLowerCase().includes(search.toLowerCase()));
    }, [students, search]);

    return (
        <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100vh', p: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1a237e' }}>
                    Document Manager
                </Typography>
                <TextField
                    placeholder="Search name..."
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ width: 300, backgroundColor: 'white' }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
                    }}
                />
            </Stack>

            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                {fetching ? <Box textAlign="center" p={5}><CircularProgress /></Box> : (
                    <Table size='small'>
                        <TableHead sx={{ backgroundColor: '#f0f2f5' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', width: '50px' }}>Sr.</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Student Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Contact</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((stu, index) => (
                                <TableRow key={stu.id} hover>
                                    <TableCell>{(page * rowsPerPage) + index + 1}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            {/* <Avatar sx={{ width: 28, height: 28, fontSize: '0.8rem', bgcolor: '#3f51b5' }}>
                                                {stu.studentName?.charAt(0)}
                                            </Avatar> */}
                                            <Typography variant="body2" fontWeight={600}>{stu.studentName}</Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.8rem' }}>{stu.father_mobile1}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                            {documents.map(d => {
                                                const isUploaded = stu.documents && stu.documents[d.docname];
                                                return (
                                                    <Tooltip key={d.id} title={isUploaded ? "Click to view" : "Missing"}>
                                                        <Chip
                                                            size="small"
                                                            label={d.docname}
                                                            color={isUploaded ? "success" : "default"}
                                                            onClick={isUploaded ? () => handlePreview(stu.documents[d.docname].url) : undefined}
                                                            onDelete={isUploaded ? () => handleDeleteDoc(stu.id, d.docname, stu.documents[d.docname].path) : undefined}
                                                            sx={{ height: 20, fontSize: '0.65rem' }}
                                                        />
                                                    </Tooltip>
                                                );
                                            })}
                                        </Stack>
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" color="primary" onClick={() => handleOpen(stu)}>
                                            <CloudUpload fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={filtered.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, nP) => setPage(nP)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
                />
            </TableContainer>

            {/* Upload Dialog */}
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
                <DialogTitle>Quick Upload</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        {documents.map((docType) => (
                            <Box key={docType.id} sx={{ p: 1.5, border: '1px dashed #ddd', borderRadius: 2 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2">{docType.docname}</Typography>
                                    <Button variant="outlined" component="label" size="small" sx={{ fontSize: '10px' }}>
                                        {files[docType.docname] ? "Changed" : "Select"}
                                        <input hidden type="file" onChange={(e) => setFiles({ ...files, [docType.docname]: e.target.files[0] })} />
                                    </Button>
                                </Stack>
                                {files[docType.docname] && (
                                    <Typography variant="caption" color="success.main" noWrap sx={{ display: 'block' }}>
                                        ✓ {files[docType.docname].name}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} size="small">Cancel</Button>
                    <Button
                        onClick={handleUpload}
                        disabled={loading || Object.keys(files).length === 0}
                        variant="contained"
                        size="small"
                    >
                        {loading ? <CircularProgress size={20} /> : "Upload All"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Preview */}
            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md">
                <img src={previewUrl} alt="Preview" style={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
            </Dialog>
        </Box>
    );
}

export default DocumentUpload;